// Speil av functions/accounting/ledgerBalances.ts — lesemodellen for saldoer per måned
// (docs/architecture/row-per-document-scaling-plan.md, avsnitt 5; hastighetssporet S3).
//
// Et foretak med markøren `readModels.ledgerBalances.status = 'ready'` i rotfeltet på app/state
// har ett dokument per regnskapsmåned i `businesses/{id}/ledgerBalances/{yyyy-MM}` med
// bevegelse per konto (`accounts`), åpningsbalanse-delen av den (`opening`) og MVA-grunnlag
// per nøkkel (`vat`), alt i hele øre. Serveren legger hver ny journalhendelse til dokumentet i
// samme transaksjon som hendelsen, så modellen er aldri bak journalen.
//
// Klienten lytter på hele samlingen (én liten rad per måned) og legger den på tilstanden som
// `state.ledgerBalancesByMonth` og `state.ledgerBalancesStatus`. Regnskapsmotoren bruker
// den i stedet for å replaye journalen når rapporten er månedsjustert og uten
// dimensjonsfilter (backend-accounting-engine.jsx, backend-vat-report.jsx). Modellen regnes
// som brukbar bare når høyeste `last_seq` er lik foretakets løpenummer, altså når lytteren
// har fått med seg den siste hendelsen.

(function () {
  const READ_MODELS_KEY = 'readModels';
  const LEDGER_BALANCES_MODEL = 'ledgerBalances';
  const LEDGER_BALANCES_VERSION = 1;
  const COLLECTION = 'ledgerBalances';
  const BY_MONTH_KEY = 'ledgerBalancesByMonth';
  const STATUS_KEY = 'ledgerBalancesStatus';

  const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
  const num = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

  const ledgerBalancesMarker = (appDocData) => {
    const marker = appDocData?.[READ_MODELS_KEY]?.[LEDGER_BALANCES_MODEL];
    if (!isPlainObject(marker) || typeof marker.status !== 'string') return null;
    return { status: marker.status, version: num(marker.version), verifiedThroughSeq: num(marker.verifiedThroughSeq) };
  };
  const ledgerBalancesReady = (appDocData) => {
    const marker = ledgerBalancesMarker(appDocData);
    return Boolean(marker && marker.status === 'ready' && marker.version === LEDGER_BALANCES_VERSION);
  };

  // Månedene i stigende rekkefølge; last_seq er høyeste løpenummer modellen har sett.
  const mergeLedgerBalances = (appState, documents) => {
    const byMonth = {};
    let lastSeq = 0;
    for (const [month, data] of [...documents].sort(([left], [right]) => left.localeCompare(right))) {
      if (!isPlainObject(data)) continue;
      byMonth[month] = {
        accounts: isPlainObject(data.accounts) ? data.accounts : {},
        opening: isPlainObject(data.opening) ? data.opening : {},
        vat: isPlainObject(data.vat) ? data.vat : {},
        event_count: num(data.event_count),
        last_seq: num(data.last_seq),
      };
      lastSeq = Math.max(lastSeq, num(data.last_seq));
    }
    return { ...(isPlainObject(appState) ? appState : {}), [BY_MONTH_KEY]: byMonth, [STATUS_KEY]: { status: 'READY', version: LEDGER_BALANCES_VERSION, last_seq: lastSeq, months: Object.keys(byMonth).length } };
  };

  // Brukbar for rapporter: modellen finnes og har fått med seg den siste hendelsen.
  const ledgerBalancesUsable = (state) => isPlainObject(state?.[BY_MONTH_KEY]) && state?.[STATUS_KEY]?.status === 'READY'
    && num(state[STATUS_KEY].last_seq) === num(state.accountingRecordedSeqCounter);

  // Bare i klienten: lytting (brukes av backend-state-access.jsx).
  const createLedgerSubscription = ({ businessRef, isCurrent, onChange, onError }) => {
    let active = false;
    let arrived = false;
    let unsubscribe = null;
    const documents = new Map();
    const detach = () => { if (unsubscribe) unsubscribe(); unsubscribe = null; arrived = false; documents.clear(); };
    return {
      // Kalles ved hvert app/state-øyeblikksbilde.
      setLayout(appDocData) {
        const nextActive = ledgerBalancesReady(appDocData);
        if (nextActive === active && (unsubscribe || !nextActive)) return;
        detach();
        active = nextActive;
        if (!active) { onChange(); return; }
        const query = businessRef.collection(COLLECTION);
        unsubscribe = query.onSnapshot(
          (snapshot) => {
            if (!isCurrent()) return;
            snapshot.docChanges().forEach((change) => {
              if (change.type === 'removed') documents.delete(change.doc.id);
              else documents.set(change.doc.id, change.doc.data() || {});
            });
            arrived = true;
            onChange();
          },
          (error) => { if (isCurrent()) onError(error); },
        );
      },
      ready: () => !active || arrived,
      // Only the authorized collection can supply transient monthly balances.
      merge: (appState) => {
        if (active) return mergeLedgerBalances(appState, documents);
        if (!isPlainObject(appState) || (!(BY_MONTH_KEY in appState) && !(STATUS_KEY in appState))) return appState;
        const clean = { ...appState }; delete clean[BY_MONTH_KEY]; delete clean[STATUS_KEY]; return clean;
      },
      active: () => active,
      stop() { detach(); active = false; },
    };
  };

  window.PlummeLedgerBalances = {
    READ_MODELS_KEY, LEDGER_BALANCES_MODEL, LEDGER_BALANCES_VERSION, COLLECTION, BY_MONTH_KEY, STATUS_KEY,
    ledgerBalancesMarker, ledgerBalancesReady, mergeLedgerBalances, ledgerBalancesUsable, createLedgerSubscription,
  };
})();
